import React, { useEffect, useState } from 'react';

import {
    GetServiceLaunchDateInput,
    GetServiceLaunchDateOutput,
    ServiceLaunch,
    ServiceLaunches
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import minusCircleTokens from '@amzn/meridian-tokens/base/icon/minus-circle';
import plusCircleTokens from '@amzn/meridian-tokens/base/icon/plus-circle';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import Input from '@amzn/meridian/input';
import InputMask from '@amzn/meridian/input/mask';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';

import { contractConfigurationClient } from 'src/api/client';
import { TerritoryFlag } from 'src/components/TerritoryFlag';
import { ALL_SERVICE, GLOBAL_TERRITORY } from 'src/constants/context';
import { AppContext } from 'src/contexts/appContext';
import { IMetadata } from 'src/types/IMetadata';
import { IVendorCodeOverride } from 'src/types/IVendor';
import { dateYyyyMmDdMask } from 'src/util/inputMask';
import { buildServiceLaunchDateMap } from 'src/util/metadataUtil';

import './VendorCodeOverrides.scss';

interface IVendorCodeOverridesProps {
    vendorCodeOverrides: IVendorCodeOverride[];
    addVendorCodeOverride: (isOverride: boolean) => void;
    removeVendorCodeOverride: (vendorCodeId: string, isOverride: boolean) => void;
    updateVendorCodeOverride: (
        vendorCode: IVendorCodeOverride,
        value: string,
        field: string,
        vendorCodeId: string,
        isOverride: boolean
    ) => void;
    validateField: (field: string, vendorCodeId?: string, isOverride?: boolean) => void;
    errorMessages: Record<string, string>;
}

export const VendorCodeOverrides = ({
    vendorCodeOverrides,
    addVendorCodeOverride,
    removeVendorCodeOverride,
    updateVendorCodeOverride,
    validateField,
    errorMessages
}: IVendorCodeOverridesProps) => {
    const catalogCodeMask = new InputMask('xxxx');

    const { metadata, setTopAlertMsg } = React.useContext(AppContext);
    const services = Object.values(metadata.serviceMapping);
    const [serviceLaunchMap, setServiceLaunchMap] = useState<Record<string, ServiceLaunches>>({});

    // Call service launch date API
    useEffect(() => {
        contractConfigurationClient.getServiceLaunchDate(GetServiceLaunchDateInput.clone({})).subscribe(
            (response: GetServiceLaunchDateOutput) =>
                setServiceLaunchMap(buildServiceLaunchDateMap(response.serviceLaunches ?? [])),
            (error: UnknownCoralError) => setTopAlertMsg(error.receivedException.message)
        );
    }, []);

    // Clear territory="Global" when service="All"
    useEffect(() => {
        vendorCodeOverrides.forEach((vendorCodeOverride) => {
            if (
                vendorCodeOverride.serviceId === ALL_SERVICE.ID &&
                vendorCodeOverride.territoryId === GLOBAL_TERRITORY.ID
            ) {
                updateVendorCodeOverride(vendorCodeOverride, '', 'territoryId', vendorCodeOverride.vendorCodeId, true);
            }
        });
    }, [JSON.stringify(vendorCodeOverrides)]);

    const renderTerritoryOptions = (vendorCodeOverride: IVendorCodeOverride, metadata: IMetadata) => {
        const isAllService = vendorCodeOverride.serviceId === ALL_SERVICE.ID;

        // Get the relevant service launch dates based on the selected service
        const serviceLaunchDates: ServiceLaunch[] = isAllService
            ? Object.values(serviceLaunchMap).flat()
            : (serviceLaunchMap[vendorCodeOverride.serviceId!] ?? []);

        // Create an array of territory options for the Select component
        const options = Array.from(
            new Map(
                serviceLaunchDates
                    .filter(({ territoryID, territoryCode }) => territoryID && territoryCode)
                    .map(({ territoryID, territoryCode }) => [territoryID!, territoryCode!])
            ).entries()
        )
            // Sort the territory options by territory code ASC
            .sort(([, a], [, b]) => a.localeCompare(b))
            .map(([territoryID, territoryCode]) => (
                <SelectOption value={territoryID} label={territoryCode} key={territoryID}>
                    {() => <TerritoryFlag territoryCode={territoryCode} />}
                </SelectOption>
            ));

        // Conditionally add Global territory option for individual services
        if (!isAllService) {
            options.unshift(
                <SelectOption value={GLOBAL_TERRITORY.ID} label={GLOBAL_TERRITORY.NAME} key={GLOBAL_TERRITORY.ID}>
                    {() => <TerritoryFlag territoryCode={GLOBAL_TERRITORY.NAME} />}
                </SelectOption>
            );
        }

        return options;
    };

    return (
        <Column>
            <Heading level={4}>Vendor code overrides (optional)</Heading>
            {vendorCodeOverrides.map((vendorCodeOverride) => (
                <Row
                    key={vendorCodeOverride.vendorCodeId}
                    alignmentVertical="top"
                    widths={['15%', '15%', '15%', '15%', '15%', '15%']}
                >
                    <Input
                        label="Catalog code *"
                        value={vendorCodeOverride.catalogCode}
                        onChange={(value) =>
                            updateVendorCodeOverride(
                                vendorCodeOverride,
                                value.toUpperCase(),
                                'catalogCode',
                                vendorCodeOverride.vendorCodeId,
                                true
                            )
                        }
                        onBlur={() => validateField('catalogCode', vendorCodeOverride.vendorCodeId, true)}
                        error={!!errorMessages[`vendorCodeOverrides${vendorCodeOverride.vendorCodeId}catalogCode`]}
                        errorMessage={errorMessages[`vendorCodeOverrides${vendorCodeOverride.vendorCodeId}catalogCode`]}
                        mask={catalogCodeMask}
                        type="text"
                        size="large"
                    />
                    <Input
                        label="Royalty code *"
                        value={vendorCodeOverride.royaltyCode}
                        onChange={(value) =>
                            updateVendorCodeOverride(
                                vendorCodeOverride,
                                value.toUpperCase(),
                                'royaltyCode',
                                vendorCodeOverride.vendorCodeId,
                                true
                            )
                        }
                        onBlur={() => validateField('royaltyCode', vendorCodeOverride.vendorCodeId, true)}
                        error={!!errorMessages[`vendorCodeOverrides${vendorCodeOverride.vendorCodeId}royaltyCode`]}
                        errorMessage={errorMessages[`vendorCodeOverrides${vendorCodeOverride.vendorCodeId}royaltyCode`]}
                        type="text"
                        size="large"
                    />
                    <Select
                        label="Service *"
                        value={vendorCodeOverride.serviceId}
                        onChange={(value) =>
                            updateVendorCodeOverride(
                                vendorCodeOverride,
                                value,
                                'serviceId',
                                vendorCodeOverride.vendorCodeId,
                                true
                            )
                        }
                        onBlur={() => validateField('serviceId', vendorCodeOverride.vendorCodeId, true)}
                        error={!!errorMessages[`vendorCodeOverrides${vendorCodeOverride.vendorCodeId}serviceId`]}
                        errorMessage={errorMessages[`vendorCodeOverrides${vendorCodeOverride.vendorCodeId}serviceId`]}
                        size="large"
                    >
                        {services.map((service, index) => (
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            <SelectOption value={service.serviceId!} label={service.serviceName!} key={index} />
                        ))}
                    </Select>
                    <Select
                        label="Territory *"
                        value={vendorCodeOverride.territoryId}
                        onChange={(value) =>
                            updateVendorCodeOverride(
                                vendorCodeOverride,
                                value,
                                'territoryId',
                                vendorCodeOverride.vendorCodeId,
                                true
                            )
                        }
                        onBlur={() => validateField('territoryId', vendorCodeOverride.vendorCodeId, true)}
                        error={!!errorMessages[`vendorCodeOverrides${vendorCodeOverride.vendorCodeId}territoryId`]}
                        errorMessage={errorMessages[`vendorCodeOverrides${vendorCodeOverride.vendorCodeId}territoryId`]}
                        disabled={vendorCodeOverride.serviceId === undefined}
                        size="large"
                    >
                        {renderTerritoryOptions(vendorCodeOverride, metadata)}
                    </Select>
                    <Input
                        label="Mapping start *"
                        value={vendorCodeOverride.mappingStart}
                        onChange={(value) =>
                            updateVendorCodeOverride(
                                vendorCodeOverride,
                                value,
                                'mappingStart',
                                vendorCodeOverride.vendorCodeId,
                                true
                            )
                        }
                        onBlur={() => validateField('mappingStart', vendorCodeOverride.vendorCodeId, true)}
                        error={!!errorMessages[`vendorCodeOverrides${vendorCodeOverride.vendorCodeId}mappingStart`]}
                        errorMessage={
                            errorMessages[`vendorCodeOverrides${vendorCodeOverride.vendorCodeId}mappingStart`]
                        }
                        mask={dateYyyyMmDdMask}
                        type="text"
                        size="large"
                        placeholder="YYYY-MM-DD"
                    />
                    <Input
                        label="Mapping end"
                        value={vendorCodeOverride.mappingEnd}
                        onChange={(value) =>
                            updateVendorCodeOverride(
                                vendorCodeOverride,
                                value,
                                'mappingEnd',
                                vendorCodeOverride.vendorCodeId,
                                true
                            )
                        }
                        onBlur={() => validateField('mappingEnd', vendorCodeOverride.vendorCodeId, true)}
                        error={!!errorMessages[`vendorCodeOverrides${vendorCodeOverride.vendorCodeId}mappingEnd`]}
                        errorMessage={errorMessages[`vendorCodeOverrides${vendorCodeOverride.vendorCodeId}mappingEnd`]}
                        mask={dateYyyyMmDdMask}
                        type="text"
                        size="large"
                        placeholder="YYYY-MM-DD"
                    />
                    <div className="remove-vendor-code-btn">
                        <Button
                            type="link"
                            size="small"
                            onClick={() => removeVendorCodeOverride(vendorCodeOverride.vendorCodeId, true)}
                        >
                            <Icon tokens={minusCircleTokens} />
                        </Button>
                    </div>
                </Row>
            ))}
            <div>
                <Button type="link" size="small" onClick={() => addVendorCodeOverride(true)}>
                    <Icon tokens={plusCircleTokens} />
                    Add override
                </Button>
            </div>
        </Column>
    );
};
